import { startAuthentication, startRegistration, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill } from '@simplewebauthn/browser';
import app from 'flarum/forum/app';

/**
 * Discriminated error class so callers can branch on cause without parsing
 * messages. The browser surfaces a {@link DOMException} for almost every
 * failure mode (NotAllowedError for cancellation or timeout, NotSupportedError
 * when no compatible authenticator is available, InvalidStateError when a
 * credential is already enrolled). We translate those into a single shape.
 */
export class PasskeyClientError extends Error {
  constructor(kind, message, cause) {
    super(message);
    this.kind = kind;
    this.cause = cause;
    this.name = 'PasskeyClientError';
  }
}
export function isSupported() {
  return browserSupportsWebAuthn();
}
export async function isAutofillSupported() {
  try {
    return await browserSupportsWebAuthnAutofill();
  } catch (_unused) {
    return false;
  }
}
export async function fetchRegistrationOptions() {
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/passkey/registration-options'
  });
}
export async function performRegistration(options, useAutoRegister) {
  if (useAutoRegister === void 0) {
    useAutoRegister = false;
  }
  try {
    return await startRegistration({
      optionsJSON: options,
      useAutoRegister
    });
  } catch (err) {
    throw mapWebAuthnError(err);
  }
}
export async function submitRegistration(credential, deviceName) {
  return app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/passkey/registration',
    body: {
      credential,
      deviceName
    }
  });
}
export async function fetchLoginOptions() {
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/passkey/login-options'
  });
}
export async function performAuthentication(options, useBrowserAutofill) {
  if (useBrowserAutofill === void 0) {
    useBrowserAutofill = false;
  }
  try {
    return await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill
    });
  } catch (err) {
    throw mapWebAuthnError(err);
  }
}
export async function submitLogin(credential, remember) {
  if (remember === void 0) {
    remember = true;
  }
  return app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/passkey/login',
    body: {
      credential,
      remember
    }
  });
}
export async function bulkRevoke() {
  await app.request({
    method: 'DELETE',
    url: app.forum.attribute('apiUrl') + '/passkey/bulk-revoke'
  });
}
function mapWebAuthnError(err) {
  if (!(err instanceof Error)) {
    return new PasskeyClientError('unknown', 'Unknown error', err);
  }

  // The conditional-mediation flow can also throw an AbortError when a fresh
  // ceremony cancels the in-flight one; treat as a normal cancellation.
  if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
    return new PasskeyClientError('cancelled', err.message, err);
  }
  if (err.name === 'InvalidStateError') {
    return new PasskeyClientError('invalid_state', err.message, err);
  }
  if (err.name === 'SecurityError') {
    return new PasskeyClientError('security', err.message, err);
  }
  if (err.name === 'NotSupportedError') {
    return new PasskeyClientError('unsupported', err.message, err);
  }
  return new PasskeyClientError('unknown', err.message, err);
}
flarum.reg.add('datlechin-passkey', 'forum/lib/webauthn', { PasskeyClientError: PasskeyClientError,isSupported: isSupported, });